import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { CONDITIONS, SORTS, RATING_OPTIONS } from "@/lib/constants";

const ANY = "all";

export const HomeSearch = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [f, setF] = useState({
        q: "", category: ANY, specialty: ANY, condition: ANY,
        min_price: "", max_price: "", min_rating: ANY, sort: "ending_soon",
    });

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
        api.get("/specialties").then((r) => setSpecialties(r.data?.specialties || [])).catch(() => {});
    }, []);

    const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

    const activeCount = [
        f.category !== ANY, f.specialty !== ANY, f.condition !== ANY,
        f.min_price !== "", f.max_price !== "", f.min_rating !== ANY,
    ].filter(Boolean).length;

    const submit = (e) => {
        e?.preventDefault?.();
        const params = new URLSearchParams();
        if (f.q.trim()) params.set("q", f.q.trim());
        if (f.category !== ANY) params.set("category", f.category);
        if (f.specialty !== ANY) params.set("specialty", f.specialty);
        if (f.condition !== ANY) params.set("condition", f.condition);
        if (f.min_price !== "") params.set("min_price", f.min_price);
        if (f.max_price !== "") params.set("max_price", f.max_price);
        if (f.min_rating !== ANY) params.set("min_rating", f.min_rating);
        if (f.sort) params.set("sort", f.sort);
        navigate(`/auctions?${params.toString()}`);
    };

    const reset = () => setF({
        q: "", category: ANY, specialty: ANY, condition: ANY,
        min_price: "", max_price: "", min_rating: ANY, sort: "ending_soon",
    });

    return (
        <Card data-testid="home-search" className="border-border bg-card p-4 shadow-[0_12px_40px_rgba(0,0,0,0.25)] sm:p-5">
            <form onSubmit={submit}>
                {/* Primary row: keyword + category + search */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                    <div className="relative flex-1">
                        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            data-testid="home-search-q"
                            value={f.q}
                            onChange={(e) => set("q")(e.target.value)}
                            placeholder="Search products, e.g. iPhone, Rolex, vintage camera…"
                            className="h-12 pl-10 text-base"
                            aria-label="Search products"
                        />
                    </div>
                    <div className="w-full lg:w-56">
                        <Select value={f.category} onValueChange={set("category")}>
                            <SelectTrigger data-testid="home-search-category" className="h-12">
                                <SelectValue placeholder="All categories" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>All categories</SelectItem>
                                {categories.map((c) => (
                                    <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button type="submit" data-testid="home-search-submit" size="lg" className="h-12 rounded-[10px] px-7 font-semibold">
                        <Search className="mr-1.5 h-4 w-4" /> Search
                    </Button>
                </div>

                {/* Filters toggle */}
                <div className="mt-3 flex items-center justify-between">
                    <button
                        type="button"
                        data-testid="home-search-toggle-filters"
                        onClick={() => setShowFilters((s) => !s)}
                        className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <SlidersHorizontal className="h-4 w-4" />
                        Advanced filters
                        {activeCount > 0 && (
                            <span className="rounded-full bg-brass/20 px-2 py-0.5 text-xs font-semibold text-brass">{activeCount}</span>
                        )}
                    </button>
                    {(activeCount > 0 || f.q) && (
                        <button type="button" data-testid="home-search-clear" onClick={reset} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                            <X className="h-3.5 w-3.5" /> Clear
                        </button>
                    )}
                </div>

                {/* Advanced filters panel */}
                {showFilters && (
                    <div data-testid="home-search-filters" className="mt-4 grid grid-cols-1 gap-4 border-t border-border pt-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Specialty</Label>
                            <Select value={f.specialty} onValueChange={set("specialty")}>
                                <SelectTrigger data-testid="home-search-specialty"><SelectValue placeholder="Any specialty" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ANY}>Any specialty</SelectItem>
                                    {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Condition</Label>
                            <Select value={f.condition} onValueChange={set("condition")}>
                                <SelectTrigger data-testid="home-search-condition"><SelectValue placeholder="Any condition" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ANY}>Any condition</SelectItem>
                                    {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Seller rating</Label>
                            <Select value={f.min_rating} onValueChange={set("min_rating")}>
                                <SelectTrigger data-testid="home-search-rating"><SelectValue placeholder="Any rating" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ANY}>Any rating</SelectItem>
                                    {RATING_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Min price ($)</Label>
                            <Input data-testid="home-search-min-price" type="number" min="0" value={f.min_price} onChange={(e) => set("min_price")(e.target.value)} placeholder="0" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Max price ($)</Label>
                            <Input data-testid="home-search-max-price" type="number" min="0" value={f.max_price} onChange={(e) => set("max_price")(e.target.value)} placeholder="Any" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Sort by</Label>
                            <Select value={f.sort} onValueChange={set("sort")}>
                                <SelectTrigger data-testid="home-search-sort"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}
            </form>
        </Card>
    );
};
